/**
 * Field — labelled input following the EZSpot input-system spec.
 *
 * Anatomy:
 *   ┌─────────────────────────────────────────────────┐
 *   │  [icon]  LABEL (uppercase caption)              │
 *   │          value / placeholder text               │
 *   └─────────────────────────────────────────────────┘
 *   [error message]
 *
 * Three states (driven by the container, not the <input>):
 *   default  — ink-05 background, ink-20 border
 *   focused  — white background, blue border + glow
 *   error    — white background, red border + glow, red label & icon
 *
 * Usage:
 *   <Field id="dest" label="Destination" icon={<MapPin />}
 *          placeholder="Address, neighborhood, or landmark…" />
 *
 *   <Field id="until" label="Until" icon={<Calendar />}
 *          value="Today, 10:00 PM" onChange={…} />
 *
 *   <Field id="dest" label="Destination" icon={<MapPin />}
 *          error="Please enter a valid San Francisco address." />
 */
"use client";

import * as React from "react";
import { cn } from "~/lib/utils";

export interface FieldProps extends Omit<React.ComponentProps<"input">, "id"> {
  /** Unique id — wires the <label> htmlFor and aria-describedby */
  id: string;
  /** Uppercase caption rendered above the value */
  label: string;
  /** Optional icon rendered to the left of the label + value stack */
  icon?: React.ReactNode;
  /** Validation error — shows red ring and message below */
  error?: string;
  /** Extra className on the outer wrapper div */
  wrapperClassName?: string;
}

export const Field = React.forwardRef<HTMLInputElement, FieldProps>(
  (
    {
      id,
      label,
      icon,
      error,
      className,
      wrapperClassName,
      disabled,
      ...inputProps
    },
    ref,
  ) => {
    const hasError = Boolean(error);
    const errorId = `${id}-error`;

    return (
      <div className={cn("flex flex-col gap-1.5", wrapperClassName)}>
        {/* ── Container ── */}
        <div
          className={cn(
            // Shape & spacing
            "group flex items-center gap-3 rounded-[var(--r-lg)] px-4 py-3.5",
            // Default surface
            "border bg-[var(--ink-05)]",
            "border-[var(--ink-20)]",
            // Transitions
            "[transition:border-color_var(--dur-fast)_var(--ease-snap),background-color_var(--dur-fast)_var(--ease-snap),box-shadow_var(--dur-fast)_var(--ease-snap)]",
            // Focus-within — blue ring
            "focus-within:border-[var(--blue)] focus-within:bg-white",
            "focus-within:shadow-[0_0_0_3px_rgba(0,115,233,0.12)]",
            // Error state overrides
            hasError && [
              "border-[var(--red)] bg-white",
              "shadow-[0_0_0_3px_rgba(255,59,48,0.10)]",
              "focus-within:border-[var(--red)] focus-within:shadow-[0_0_0_3px_rgba(255,59,48,0.14)]",
            ],
            // Disabled
            disabled && "pointer-events-none opacity-50",
          )}
        >
          {/* Icon slot */}
          {icon !== undefined && (
            <span
              aria-hidden
              className={cn(
                "shrink-0 [&_svg]:size-4",
                // Default: muted icon
                "text-[var(--ink-40)]",
                // Focus-within: blue icon
                "group-focus-within:text-[var(--blue)]",
                // Error: red icon
                hasError &&
                  "text-[var(--red)] group-focus-within:text-[var(--red)]",
              )}
            >
              {icon}
            </span>
          )}

          {/* Label + input stack */}
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            {/* Caption label */}
            <label
              htmlFor={id}
              className={cn(
                "font-ui text-[10px] font-semibold uppercase leading-none tracking-[0.08em] select-none",
                // Default + focused: blue
                "text-[var(--blue)]",
                // Error: red
                hasError && "text-[var(--red)]",
              )}
            >
              {label}
            </label>

            {/* Native input — visually transparent, inherits container sizing */}
            <input
              ref={ref}
              id={id}
              disabled={disabled}
              aria-invalid={hasError ? "true" : undefined}
              aria-describedby={hasError ? errorId : undefined}
              className={cn(
                "w-full bg-transparent outline-none",
                "font-ui text-[15px] font-medium leading-snug",
                "text-[var(--ink)]",
                "placeholder:font-normal placeholder:text-[var(--ink-40)]",
                "[transition:color_var(--dur-fast)_var(--ease-snap)]",
                // Error: red value text
                hasError &&
                  "text-[var(--red)] placeholder:text-[rgba(255,59,48,0.55)]",
                className,
              )}
              {...inputProps}
            />
          </div>
        </div>

        {/* Error message */}
        {hasError && (
          <p
            id={errorId}
            role="alert"
            className="font-ui px-1 text-[12px] font-medium text-[var(--red)]"
          >
            {error}
          </p>
        )}
      </div>
    );
  },
);

Field.displayName = "Field";
